import * as React from "react";
import { logger } from "bernie-core";
import { Experiment, isVariantEnabled } from "components/utility/experiment";
import { inject } from "mobx-react";
import { CustomerNotifications } from "components/shared/CustomerNotifications";
import { CustomerNotificationsWithDialogWrapper } from "src/components/shared/CustomerNotifications/CustomerNotificationsWithDialogWrapper";
import {
  StorefrontWizardRegionMesoProps,
  StorefrontWizardRegionMesoFTNodeOptions,
  StorefrontWizardRegionMesoFTNode,
  WizardModuleDataOptions,
} from "./typings";
import {
  StorefrontWizardRegionTypesContext,
  StorefrontWizardRegionTypes,
} from "components/flexComponents/PageLevelProviders/WizardContextProvider/StorefrontWizardRegionTypesContext";
import { withLocalization } from "@shared-ui/localization-context";
import { LobQueryRouter } from "src/components/shared/StorefrontWizard/LobQueryRouter";
import {
  getWizardLOBFlexComponents,
  getWizardLOBFormChildren,
  getNonWizardFormChildren,
  getBiasedWizardLOBFlexComponent,
  getMesoWizardModuleData,
} from "src/components/shared/StorefrontWizard/StorefrontWizardUtils";
import {
  TabbedWizardCard,
  WizardRegionType,
} from "src/components/shared/StorefrontWizard/TabbedWizardCard/TabbedWizardCard";
import { Viewport, ViewSmall, ViewMedium } from "bernie-view";
import { LobFormWithTabs } from "src/components/shared/StorefrontWizard/LobFormWithTabs/LobFormWithTabs";
import { ReasonsToBelieveWrapper } from "src/components/shared/ReasonsToBelieve/ReasonsToBelieveWrapper";
import { Experiment as BernieExperiment, ExperimentControl, ExperimentVariant } from "@shared-ui/experiment-context";
import { getTracker } from "src/utils/TrackingUtils";
import { UitkFigure } from "uitk-react-images";
import { cropResizeSrc } from "src/components/shared/BlossomImage/BlossomImage";
import { normalizeOptionsData, hasRenderableChilren } from "src/components/flexFramework/FlexTemplateNode";

@withLocalization
@logger("WizardRegionMeso")
@inject("wizardState", "analytics", "page", "flexModuleModelStore")
export class StorefrontWizardRegionMeso extends React.Component<StorefrontWizardRegionMesoProps> {
  public render() {
    const { flexTemplateNode, flexModuleModelStore, context, wizardState, analytics, blossomComponent } = this.props;

    if (!blossomComponent) {
      return null;
    }

    //default to false if device information is not available
    wizardState.globalWizardState.setIsDesktop(context.deviceInformation);

    const options = normalizeOptionsData(
      flexTemplateNode,
      flexModuleModelStore.getModel(flexTemplateNode.id)
    ) as StorefrontWizardRegionMesoFTNodeOptions;

    if (!options || !hasRenderableChilren(flexTemplateNode, flexModuleModelStore)) {
      return null;
    }

    const [biasedWizardModule] = getBiasedWizardLOBFlexComponent(
      flexTemplateNode.childNodes as StorefrontWizardRegionMesoFTNode[]
    );

    wizardState.globalWizardState.config.useLastSearchForMultiLOB = options.useLastSearchForMultiLOB;

    const lobFlexComponentMap = {
      hotel: "wizard-hotel-pwa-v2",
      flight: "wizard-flight-pwa",
      package: "wizard-package-pwa",
      car: "wizard-car-pwa",
      rail: "wizard-lx-pwa",
      cruise: "wizard-cruise-pwa",
    };

    const { backgroundImage } = options;

    let backgroundImagePosition;

    if (options.defaultLOB && biasedWizardModule) {
      const singleWizardOptions = normalizeOptionsData(
        biasedWizardModule,
        flexModuleModelStore.getModel(biasedWizardModule.id)
      ) as StorefrontWizardRegionMesoFTNodeOptions;
      backgroundImagePosition = singleWizardOptions.backgroundImagePosition;
      wizardState.globalWizardState.config.navigation.defaultLob =
        lobFlexComponentMap[singleWizardOptions.selectedProduct];
      wizardState.globalWizardState.config.navigation.activeLob =
        lobFlexComponentMap[singleWizardOptions.selectedProduct];
    } else {
      wizardState.globalWizardState.config.navigation.defaultLob = options.defaultLOB;
      wizardState.globalWizardState.config.navigation.activeLob = options.defaultLOB;
    }

    wizardState.globalWizardState.setLastSearchedLob();

    const children = blossomComponent.renderChildren(this.props.logger, this.props.l10n, context);

    const wizardLOBs = getWizardLOBFlexComponents(flexTemplateNode.childNodes as StorefrontWizardRegionMesoFTNode[]);

    const wizardFormChildren = getWizardLOBFormChildren(children);

    const otherFormChildren = getNonWizardFormChildren(children, flexModuleModelStore);

    wizardState.globalWizardState.config.heroImageSrc =
      backgroundImage || wizardState.globalWizardState.config.heroImageSrc;

    const globalConfig = wizardState.globalWizardState.config;
    const { heroImageSrc } = globalConfig;
    const imageSrcTemp = cropResizeSrc(heroImageSrc, { width: 1400, height: 600 }, "medium");
    const uitkFigureStyle = {
      backgroundImage: `url("${imageSrcTemp}")`,
      backgroundPosition: `${backgroundImagePosition || "initial"}`,
      backgroundSize: `cover`,
    };
    if (biasedWizardModule) {
      getMesoWizardModuleData(biasedWizardModule.options as WizardModuleDataOptions, wizardState);
    }

    const isStorefrontPage = context.searchContext.pageType.indexOf("Storefront") > -1;
    const reasonsToBelieveOn = isVariantEnabled(
      this.props.context,
      Experiment.Surface_PWA_SF_Travel_Advisory_Chatbot,
      2
    );
    const wizard = `all-t-padding-twelve all-x-padding-six SimpleContainer ${
      globalConfig.halfWizard ? "halfWizard" : ""
    }`;
    const wizardRegionClassNames = `WizardRegionMeso ${reasonsToBelieveOn ? "" : "all-b-margin-six"}`;

    const heroImageFigureClassNames = ` heroImageFigure ${reasonsToBelieveOn ? "withBackgroundColor" : ""}`;

    const lobNameMap = {
      "wizard-hotel-pwa-v2": "_Hotels",
      "wizard-flight-pwa": "_Flights",
      "wizard-package-pwa": "_Packages",
      "wizard-car-pwa": "_Cars",
      "wizard-lx-pwa": "_Lx",
      "wizard-cruise-pwa": "_Cruise",
    };

    const trackOnChangeTab = (tabName: string) => {
      const moduleName = options.flexComponentName === "" ? "SFWizard" : options.flexComponentName;
      const tracker = getTracker(wizardState.canTrack, analytics, moduleName);
      const lobName = lobNameMap[tabName];
      const rfrr = `LOB_Selection_Occured${lobName || ""}`;
      tracker.trackEvent(rfrr);
    };

    /**
     *
     * Note for modules below this region:
     * Modules not part of this region do not align to the form content out of the box
     * Setting the flex manager attribute brandModulePadding to bex_padding puts a spacing__twelve on the components below this region
     * This aligns them with the form content within the card on this wizard
     * On mobile, there is no card, and this same attribute puts spacing__six on these components
     */

    /*
      This particular set of styles is to position the background image inside UitkFigure.
      This feature will hopefully be added as a standard UitkFigure property which can be configured as an enum.
    */

    return (
      <>
        <StorefrontWizardRegionTypesContext.Provider value={StorefrontWizardRegionTypes.STOREFRONT_WIZARD_REGION_BEX}>
          <div className={wizardRegionClassNames} data-testid="storefront-region">
            <div data-testid="main-region">
              <Viewport>
                <ViewSmall>
                  <>
                    <LobQueryRouter lobs={wizardLOBs} />
                    {isStorefrontPage && (
                      <CustomerNotifications location="AFTER_HEADER" className="customerNotificationSquare" />
                    )}
                    <LobFormWithTabs
                      context={context}
                      lobForms={wizardFormChildren}
                      wizardFormClassNames="all-x-padding-six"
                      tabGroupClassNames="all-l-padding-six"
                      trackOnChangeTab={trackOnChangeTab}
                    />
                  </>
                </ViewSmall>

                <ViewMedium>
                  <UitkFigure className={heroImageFigureClassNames} style={uitkFigureStyle}>
                    <LobQueryRouter lobs={wizardLOBs} />
                    {isStorefrontPage && (
                      <CustomerNotifications
                        location="AFTER_HEADER"
                        className="customerNotificationSquare all-x-padding-six all-t-padding-twelve"
                      />
                    )}
                    <TabbedWizardCard
                      context={context}
                      wizardFormChildren={wizardFormChildren}
                      wizardState={wizardState}
                      wrappingClassNames={wizard}
                      tabGroupClassNames="formAlignedTabs all-x-padding-six"
                      wizardFormClassNames="wizardOverHeroImage all-x-padding-six"
                      trackOnChangeTab={trackOnChangeTab}
                      wizardRegionType={WizardRegionType.MESO}
                    />
                  </UitkFigure>
                </ViewMedium>
              </Viewport>
              {otherFormChildren}
            </div>
          </div>
          <BernieExperiment name="Surface_PWA_SF_Travel_Advisory_Chatbot">
            <ExperimentControl />
            <ExperimentVariant bucket={1}>
              <CustomerNotificationsWithDialogWrapper
                location="IN_PAGE"
                className="customerNotificationSquare customerNotificationWithDialog all-x-padding-six"
              />
            </ExperimentVariant>
            <ExperimentVariant bucket={2}>
              <ReasonsToBelieveWrapper location="IN_PAGE" className="reasons-to-believe" />
            </ExperimentVariant>
          </BernieExperiment>
        </StorefrontWizardRegionTypesContext.Provider>
      </>
    );
  }
}

export default StorefrontWizardRegionMeso;
